import React, { useEffect, useState } from 'react';
import { Clock, PoundSterling, Info, Loader2 } from 'lucide-react';
import { ServiceType } from '../../types';
import { fetchServices } from '../../services/api';
import PolicyModal from './PolicyModal';

interface ServiceSelectorProps {
  onServiceSelect: (serviceId: number) => void;
}

const ServiceSelector: React.FC<ServiceSelectorProps> = ({ onServiceSelect }) => {
  const [services, setServices] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingServiceId, setLoadingServiceId] = useState<number | null>(null);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [selectedService, setSelectedService] = useState<ServiceType | null>(null);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const data = await fetchServices();
        setServices(data);
      } catch (err) {
        setError('Failed to load services. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadServices();
  }, []);

  const handleServiceClick = (service: ServiceType) => {
    setSelectedService(service);
    setShowPolicyModal(true);
  };

  const handlePolicyAccept = async () => {
    if (!selectedService) return;
    
    setShowPolicyModal(false);
    setLoadingServiceId(selectedService.id);
    
    try {
      await onServiceSelect(selectedService.id);
    } finally {
      setLoadingServiceId(null);
      setSelectedService(null);
    }
  };

  const handlePolicyClose = () => {
    setShowPolicyModal(false);
    setSelectedService(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-error-600 p-4">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-lg mb-8">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="font-medium mb-1">Booking Information</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              A £20 deposit is required to secure your booking. The remaining balance is to be paid in cash or transfer on the day of your appointment.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service, index) => (
          <div 
            key={service.id} 
            onClick={() => handleServiceClick(service)}
            className="card p-6 cursor-pointer transform transition-all duration-300 hover:shadow-lg hover:-translate-y-1 animate-fade-in"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="h-48 overflow-hidden rounded-md mb-4">
              <img 
                src={service.image || '/img/placeholder.svg'} 
                alt={service.name} 
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/img/placeholder.svg';
                }}
              />
            </div>
            <h3 className="text-xl font-semibold mb-2">{service.name}</h3>
            <div className="flex items-center justify-between text-gray-600 dark:text-gray-400 mb-3">
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                <span className="text-sm">{service.duration}</span>
              </div>
              <div className="flex items-center font-medium text-primary-600">
                <PoundSterling className="w-4 h-4 mr-1" />
                <span>{service.price}</span>
              </div>
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
              {service.description}
            </p>
            <button 
              className="w-full btn-primary flex items-center justify-center"
              onClick={(e) => {
                e.stopPropagation();
                handleServiceClick(service);
              }}
              disabled={loadingServiceId === service.id}
            >
              {loadingServiceId === service.id ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                'Select'
              )}
            </button>
          </div>
        ))}
      </div>

      <PolicyModal
        isOpen={showPolicyModal}
        onClose={handlePolicyClose}
        onAccept={handlePolicyAccept}
        serviceName={selectedService?.name || ''}
      />
    </div>
  );
};

export default ServiceSelector;